import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircleQuestion, ScrollText, ShieldCheck, Bot, MessageSquare, Phone, Video, Mail, Zap, Coins, Undo2, Search, Eye, FlaskConical, Rocket, Terminal } from "lucide-react";
import { Reveal } from "@/components/fx";

const Tag = ({ s }) => {
  const c = { PASSED: "#6fd6b0", FAILED: "#ff5c7a", UNVERIFIED: "#f5b84a", BLOCKED: "#9b98b0" }[s];
  return <span className="label-mono rounded-full px-2 py-0.5 text-[9px]" style={{ background: `${c}22`, color: c }}>{s}</span>;
};

const Card = ({ icon: I, title, sub, children, testid, className = "" }) => (
  <Reveal className={className}>
    <div data-testid={testid} className="glass group h-full rounded-3xl p-6 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-white/20">
      <div className="flex items-center gap-2 text-[#a78bff]"><I className="h-5 w-5" /><span className="label-mono text-[10px]">{sub}</span></div>
      <h3 className="mt-3 font-display text-xl font-semibold text-white">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  </Reveal>
);

const QS = [["Who books — walk-ins, regulars or both?", "Both"], ["Can a barber be double-booked?", "Never"], ["Should customers get a text reminder?", "Yes, 24h before"]];
const Intent = () => {
  const [n, setN] = useState(0);
  useEffect(() => { const t = setInterval(() => setN((x) => (x + 1) % (QS.length + 1)), 1800); return () => clearInterval(t); }, []);
  return (
    <div className="space-y-2 text-sm">
      <div className="rounded-xl bg-white/5 px-3 py-2 text-white/70">“A booking app for my barbershop.”</div>
      {QS.slice(0, n).map(([q, a]) => (
        <motion.div key={q} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} className="flex items-center justify-between gap-2 rounded-xl border border-[#7a5cff]/30 px-3 py-2">
          <span className="text-white/80">{q}</span><span className="label-mono text-[10px] text-[#22d3ee]">{a}</span>
        </motion.div>
      ))}
    </div>
  );
};

const Twin = () => (
  <svg viewBox="0 0 300 130" className="w-full">
    {[["Rules", 40, 30], ["Data", 40, 100], ["Screens", 260, 30], ["Tests", 260, 100]].map(([l, x, y], i) => (
      <g key={l}>
        <motion.line x1="150" y1="65" x2={x} y2={y} stroke="#7a5cff" strokeOpacity=".5" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: i * 0.15, duration: 0.7 }} />
        <rect x={x - 30} y={y - 12} width="60" height="24" rx="12" fill="#13101f" stroke="#ffffff22" /><text x={x} y={y + 4} textAnchor="middle" fontSize="10" fill="#cfcbe6">{l}</text>
      </g>
    ))}
    <motion.circle cx="150" cy="65" r="26" fill="#7a5cff22" stroke="#a78bff" animate={{ r: [24, 28, 24] }} transition={{ repeat: Infinity, duration: 2.4 }} />
    <text x="150" y="62" textAnchor="middle" fontSize="9" fill="#fff">Constitution</text><text x="150" y="74" textAnchor="middle" fontSize="8" fill="#a78bff">v7</text>
  </svg>
);

const CHECKS = [["Rule: no double booking", "PASSED"], ["Journey: book a haircut", "PASSED"], ["Journey: cancel booking", "FAILED"], ["Journey: admin export", "UNVERIFIED"]];
const Reality = () => (
  <ul className="space-y-2 text-sm">
    {CHECKS.map(([n, s], i) => (
      <motion.li key={n} initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.18 }} className="flex items-center justify-between rounded-xl bg-white/[0.04] px-3 py-2">
        <span className="text-white/75">{n}</span><Tag s={s} />
      </motion.li>
    ))}
    <li className="text-[11px] text-white/45">Each result links to screenshots, requests and responses. Not verified means not verified.</li>
  </ul>
);

const STEPS = [
  [Search, "Impact", "“Add a waitlist” touches 2 screens, 1 new collection, 1 rule. No live data is removed."],
  [Eye, "Draft", "Changes are built on a sandbox copy. See the diff and click through the draft preview."],
  [FlaskConical, "Test", "Acceptance + regression tests run on the draft: 23 PASSED, 1 FAILED, with evidence."],
  [Rocket, "Publish or discard", "Publish (failures need your explicit sign-off) or throw the draft away. Roll back any version."],
];
const ChangeStepper = () => {
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(true);
  useEffect(() => { if (!auto) return undefined; const t = setInterval(() => setI((x) => (x + 1) % STEPS.length), 2600); return () => clearInterval(t); }, [auto]);
  const [I, title, body] = STEPS[i];
  return (
    <section id="why" data-testid="change-flow-section" className="mx-auto max-w-6xl px-6 py-20 md:py-28">
      <Reveal><div className="label-mono text-[#22d3ee]">The signature move</div>
        <h2 className="mt-3 font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl">Show me before<br />you change it.</h2>
        <p className="mt-4 max-w-xl text-base text-white/60 md:text-lg">Other builders edit your live app and hope. FORGE gives you four steps and a way back.</p></Reveal>
      <div className="mt-12 grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <ol className="relative space-y-3">
          <div className="absolute bottom-6 left-[22px] top-6 w-px bg-white/10" />
          <motion.div className="absolute left-[22px] top-6 w-px bg-gradient-to-b from-[#7a5cff] to-[#ff5c7a]" animate={{ height: `${(i / (STEPS.length - 1)) * 80}%` }} transition={{ duration: 0.5 }} />
          {STEPS.map(([Ic, t], k) => (
            <li key={t}>
              <button data-testid={`change-step-${k}`} onClick={() => { setAuto(false); setI(k); }} className={`relative flex w-full items-center gap-4 rounded-2xl px-2 py-3 text-left transition-colors duration-300 ${k === i ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"}`}>
                <span className={`relative z-10 grid h-11 w-11 place-items-center rounded-full border transition-colors duration-300 ${k <= i ? "border-[#7a5cff] bg-[#7a5cff]/25 text-white" : "border-white/15 bg-[#0b0914] text-white/40"}`}><Ic className="h-5 w-5" /></span>
                <span><span className="label-mono text-[10px] text-white/40">Step {k + 1}</span><span className={`block font-display text-lg ${k === i ? "text-white" : "text-white/55"}`}>{t}</span></span>
              </button>
            </li>
          ))}
        </ol>
        <div className="glass-strong noise relative min-h-[300px] overflow-hidden rounded-3xl p-7">
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}>
              <I className="h-8 w-8 text-[#ff5c7a]" />
              <div className="mt-4 font-display text-2xl font-semibold text-white">{title}</div>
              <p className="mt-2 text-white/65">{body}</p>
              {i === 1 && <pre className="mt-5 overflow-hidden rounded-xl bg-black/40 p-3 font-mono text-[11px] leading-5"><span className="text-[#6fd6b0]">+ &lt;button data-testid="join-waitlist"&gt;Join waitlist&lt;/button&gt;</span>{"\n"}<span className="text-[#ff9aac]">- &lt;p&gt;Fully booked&lt;/p&gt;</span></pre>}
              {i === 2 && <div className="mt-5 flex flex-wrap gap-2"><Tag s="PASSED" /><Tag s="PASSED" /><Tag s="FAILED" /><Tag s="PASSED" /></div>}
              {i === 3 && <div className="mt-5 flex gap-2"><span className="rounded-full bg-fire px-4 py-1.5 text-xs font-semibold text-white">Publish v8</span><span className="rounded-full border border-white/15 px-4 py-1.5 text-xs text-white/70">Discard</span><span className="inline-flex items-center gap-1 rounded-full border border-white/15 px-4 py-1.5 text-xs text-white/70"><Undo2 className="h-3 w-3" />Roll back</span></div>}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const COPILOT = [["query", "How many bookings did Marco have last week?", "14 bookings · 2 cancelled (from live data)"], ["explain", "Why can't I book 3pm?", "Rule no_overlap: Marco is booked 2:45–3:30."], ["debug", "The cancel button does nothing", "screen-book.js:88 calls undefined cancelBooking() — draft fix ready."]];
const CopilotDemo = () => {
  const [k, setK] = useState(0);
  const [m, q, a] = COPILOT[k];
  return (
    <div>
      <div className="flex gap-1.5">{COPILOT.map(([mm], j) => <button key={mm} data-testid={`copilot-demo-${mm}`} onClick={() => setK(j)} className={`label-mono rounded-full px-2.5 py-1 text-[10px] ${j === k ? "bg-[#7a5cff]/30 text-white" : "bg-white/5 text-white/50"}`}>{mm}</button>)}</div>
      <AnimatePresence mode="wait"><motion.div key={m} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-3 space-y-2 font-mono text-xs">
        <div className="text-white/50">you › {q}</div><div className="text-[#6fd6b0]">copilot › {a}</div>
      </motion.div></AnimatePresence>
    </div>
  );
};

const CH = [[MessageSquare, "SMS"], [MessageSquare, "MMS"], [Phone, "Voice"], [Video, "Video"], [Mail, "Email"], [Zap, "Automations"]];
const Connect = () => (
  <div>
    <div className="grid grid-cols-3 gap-2">{CH.map(([I, l], i) => (
      <motion.div key={l} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.08 }} className="flex flex-col items-center gap-1 rounded-xl bg-white/[0.04] py-3 text-xs text-white/70"><I className="h-4 w-4 text-[#22d3ee]" />{l}</motion.div>
    ))}</div>
    <div className="mt-3 rounded-xl border border-white/10 px-3 py-2 font-mono text-[11px] text-white/60">booking created → if status = confirmed → text “See you {"{{start_time|time}}"}”</div>
    <p className="mt-2 text-[11px] text-white/45">Consent, STOP handling and quiet hours built in. No provider connected? It says Not sent.</p>
  </div>
);

const Cost = () => (
  <div className="grid grid-cols-2 gap-3 text-center">
    <div className="rounded-2xl bg-white/[0.04] p-4"><div className="label-mono text-[10px] text-white/45">Estimate before</div><div className="mt-1 font-display text-2xl text-white">$0.42–0.71</div></div>
    <div className="rounded-2xl bg-white/[0.04] p-4"><div className="label-mono text-[10px] text-white/45">Actual after</div><motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-1 font-display text-2xl text-[#6fd6b0]">$0.58</motion.div></div>
  </div>
);

export const Differentiators = () => (
  <div data-testid="differentiators">
    <section className="mx-auto max-w-7xl px-6 pt-20 md:pt-28">
      <Reveal><div className="label-mono text-[#a78bff]">Why builders switch to FORGE</div>
        <h2 className="mt-3 max-w-3xl font-display text-4xl font-bold text-white sm:text-5xl">Trust is the feature.</h2></Reveal>
      <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        <Card testid="diff-intent" icon={MessageCircleQuestion} sub="Intent Engine" title="It asks what matters">
          <Intent />
        </Card>
        <Card testid="diff-constitution" icon={ScrollText} sub="Application Constitution" title="A living twin of your app"><Twin /><p className="mt-2 text-xs text-white/50">Every rule, field, screen and test, versioned. Changes are checked against it.</p></Card>
        <Card testid="diff-reality" icon={ShieldCheck} sub="Reality Engine" title="Real evidence, never fake green"><Reality /></Card>
      </div>
    </section>
    <ChangeStepper />
    <section className="mx-auto max-w-7xl px-6 pb-12">
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        <Card testid="diff-copilot" icon={Terminal} sub="App Copilot" title="Query, explain, debug" className="lg:col-span-2"><CopilotDemo /></Card>
        <Card testid="diff-connect" icon={Zap} sub="FORGE Connect" title="Your app can reach people" className="lg:col-span-2"><Connect /></Card>
        <Card testid="diff-cost" icon={Coins} sub="Cost transparency" title="Know the cost up front" className="lg:col-span-2"><Cost /></Card>
        <Card testid="diff-agents" icon={Bot} sub="Dedicated AI agents" title="An agent inside each app"><p className="text-sm text-white/60">A chat agent grounded in your app's own data, with its own prompt and conversation history.</p></Card>
        <Card testid="diff-rollback" icon={Undo2} sub="Safe publish" title="Every version, reversible"><p className="text-sm text-white/60">Publish when tests pass, or acknowledge failures on the record. Roll back any version without losing live data.</p></Card>
      </div>
    </section>
  </div>
);
